import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import TagApiService, { Tag, CreateTagRequest, UpdateTagRequest } from 'app/apiService/tag/tagApiService';

// ----------- Type Definitions -----------
interface TagContextType {
  loading: boolean;
  error: string | null;
  tags: Tag[];
  meta: any;
  selectedTag: Tag | null;
  getTags: (params?: any) => Promise<void>;
  getTagById: (id: string) => Promise<void>;
  getTagsByType: (type: string) => Promise<void>;
  createTag: (data: CreateTagRequest) => Promise<void>;
  updateTag: (id: string, data: UpdateTagRequest) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  setSelectedTag: (tag: Tag | null) => void;
  clearError: () => void;
}

// ----------- Context Create -----------
const TagContext = createContext<TagContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const TagProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  const getTags = useCallback(async (params?: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TagApiService.getTags({
        ...params,
      });
      setTags(response.body?.data || []);
      setMeta(response.body?.meta || {});
    } catch (err: any) {
      setError(err?.message || 'An error occurred while fetching tags');
      console.error('Get tags error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getTagById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TagApiService.getTagById(id);
      setSelectedTag(response.body || null);
    } catch (err: any) {
      setError(err?.message || 'An error occurred while fetching tag');
      console.error('Get tag by ID error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getTagsByType = useCallback(async (type: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TagApiService.getTags({
        type: type,
      });
      setSelectedTag(response.body.data || null);
    } catch (err: any) {
      setError(err?.message || 'An error occurred while fetching tag');
      console.error('Get tag by ID error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createTag = useCallback(async (data: CreateTagRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TagApiService.createTag(data);
      // Refresh tags list after successful creation with the same type filter
      await getTags({ type: data.type });
    } catch (err: any) {
      setError(err?.message || 'An error occurred while creating tag');
      console.error('Create tag error:', err);
    } finally {
      setLoading(false);
    }
  }, [getTags]);

  const updateTag = useCallback(async (id: string, data: UpdateTagRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TagApiService.updateTag(id, data);
      
      // Update the tag in the local state
      setTags(prevTags => 
        prevTags.map(tag => 
          tag._id === id ? { ...tag, ...response.body } : tag
        )
      );
      
      // Update selected tag if it matches
      if (selectedTag && selectedTag._id === id) {
        setSelectedTag({ ...selectedTag, ...response.body });
      }
    } catch (err: any) {
      setError(err?.message || 'An error occurred while updating tag');
      console.error('Update tag error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedTag]);

  const deleteTag = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TagApiService.deleteTag(id);
      
      // Remove tag from local state
      setTags(prevTags => prevTags.filter(tag => tag._id !== id));
      
      // Clear selected tag if it matches
      if (selectedTag && selectedTag._id === id) {
        setSelectedTag(null);
      }
    } catch (err: any) {
      setError(err?.message || 'An error occurred while deleting tag');
      console.error('Delete tag error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedTag]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const contextValue: TagContextType = {
    loading,
    error,
    tags,
    meta,
    selectedTag,
    getTags,
    getTagById,
    getTagsByType,
    createTag,
    updateTag,
    deleteTag,
    setSelectedTag,
    clearError,
  };

  return (
    <TagContext.Provider value={contextValue}>
      {children}
    </TagContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useTag = (): TagContextType => {
  const context = useContext(TagContext);
  if (!context) {
    throw new Error('useTag must be used within a TagProvider');
  }
  return context;
}; 